import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { reportClientError } from '../utils/clientLogger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(true);

  const loadProjects = useCallback(async (userEmail) => {
    try {
      const response = await axios.get(`${API}/projects`, {
        params: { user_email: userEmail }
      });
      setProjects(response.data);
    } catch (error) {
      reportClientError('Error loading projects:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Check if email is stored in localStorage
    const savedEmail = localStorage.getItem('proptech_user_email');
    if (savedEmail) {
      setEmail(savedEmail);
      setShowEmailModal(false);
      loadProjects(savedEmail);
    } else {
      setLoading(false);
    }
  }, [loadProjects]);

  const handleEmailSubmit = () => {
    if (!searchEmail.trim()) return;
    localStorage.setItem('proptech_user_email', searchEmail);
    setEmail(searchEmail);
    setShowEmailModal(false);
    setLoading(true);
    loadProjects(searchEmail);
  };

  const handleLogout = () => {
    localStorage.removeItem('proptech_user_email');
    setEmail('');
    setProjects([]);
    setShowEmailModal(true);
  };

  const deleteProject = async (projectId) => {
    if (!window.confirm('Tem certeza que deseja excluir este projeto?')) return;

    try {
      await axios.delete(`${API}/projects/${projectId}`);
      setProjects(projects.filter(p => p.id !== projectId));
    } catch (error) {
      reportClientError('Error deleting project:', error);
      alert('Erro ao excluir projeto');
    }
  };

  if (showEmailModal) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center px-4">
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 max-w-md w-full border border-white/10">
          <div className="text-center mb-6">
            <div className="text-6xl mb-4">🏠</div>
            <h2 className="text-2xl font-bold text-white mb-2">Acessar Dashboard</h2>
            <p className="text-gray-400">Digite seu email para ver seus projetos</p>
          </div>
          <input
            type="email"
            value={searchEmail}
            onChange={(e) => setSearchEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleEmailSubmit()}
            placeholder="seu@email.com"
            data-testid="dashboard-email-input"
            className="w-full p-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
          />
          <button
            onClick={handleEmailSubmit}
            className="w-full py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-xl hover:from-blue-600 hover:to-purple-700 transition"
            data-testid="dashboard-email-submit-button"
          >
            Acessar Meus Projetos
          </button>
          <Link
            to="/"
            className="block text-center mt-4 text-gray-400 hover:text-white"
            data-testid="dashboard-modal-back-link"
          >
            ← Voltar ao início
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-gray-600 hover:text-gray-900" data-testid="dashboard-header-back-link">
              ← Voltar
            </Link>
            <h1 className="text-xl font-bold text-gray-900">Meus Projetos</h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-gray-600 text-sm">{email}</span>
            <button
              onClick={handleLogout}
              className="text-sm text-red-600 hover:text-red-700"
            data-testid="dashboard-logout-button"
            >
              Sair
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Link
            to="/desenhar-planta"
            className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 hover:border-blue-400 transition group"
            data-testid="dashboard-quick-desenhar-link"
          >
            <div className="text-4xl mb-3">✏️</div>
            <h3 className="font-semibold text-gray-900 group-hover:text-blue-600">Desenhar Planta</h3>
            <p className="text-gray-600 text-sm">Criar planta manualmente</p>
          </Link>
          <Link
            to="/gerar-planta"
            className="bg-purple-50 border-2 border-purple-200 rounded-xl p-6 hover:border-purple-400 transition group"
            data-testid="dashboard-quick-gerar-link"
          >
            <div className="text-4xl mb-3">🤖</div>
            <h3 className="font-semibold text-gray-900 group-hover:text-purple-600">Gerar com IA</h3>
            <p className="text-gray-600 text-sm">IA cria a planta</p>
          </Link>
          <Link
            to="/admin"
            className="bg-orange-50 border-2 border-orange-200 rounded-xl p-6 hover:border-orange-400 transition group"
            data-testid="dashboard-quick-admin-link"
          >
            <div className="text-4xl mb-3">⚙️</div>
            <h3 className="font-semibold text-gray-900 group-hover:text-orange-600">Admin</h3>
            <p className="text-gray-600 text-sm">Painel administrativo</p>
          </Link>
        </div>

        {/* Projects List */}
        <div className="bg-white rounded-xl shadow-sm">
          <div className="p-6 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Seus Projetos</h2>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <div className="w-12 h-12 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">Carregando projetos...</p>
            </div>
          ) : projects.length === 0 ? (
            <div className="p-12 text-center">
              <div className="text-6xl mb-4">📁</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhum projeto encontrado</h3>
              <p className="text-gray-600 mb-6">Comece criando seu primeiro projeto!</p>
              <div className="flex gap-4 justify-center">
                <Link
                  to="/desenhar-planta"
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  data-testid="dashboard-empty-desenhar-link"
                >
                  ✏️ Desenhar Planta
                </Link>
                <Link
                  to="/gerar-planta"
                  className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  data-testid="dashboard-empty-gerar-link"
                >
                  🤖 Gerar com IA
                </Link>
              </div>
            </div>
          ) : (
            <div className="divide-y">
              {projects.map((project) => (
                <div key={project.id} className="p-6 hover:bg-gray-50 transition">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{project.name}</h3>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                        <span>🏠 {project.floor_plan?.rooms?.length || 0} cômodos</span>
                        <span>👁️ {project.view_count_3d || 0} visualizações 3D</span>
                        <span>📅 {new Date(project.created_at).toLocaleDateString('pt-BR')}</span>
                      </div>
                      {project.description && (
                        <p className="text-sm text-gray-500 mt-2 line-clamp-1">{project.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => navigate(`/render-3d/${project.id}`)}
                        className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition"
                        data-testid={`dashboard-project-view-3d-button-${project.id}`}
                      >
                        🏠 Ver 3D
                      </button>
                      <button
                        onClick={() => navigate(`/projeto/${project.id}`)}
                        className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition"
                        data-testid={`dashboard-project-details-button-${project.id}`}
                      >
                        📋 Detalhes
                      </button>
                      <button
                        onClick={() => deleteProject(project.id)}
                        className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition"
                        data-testid={`dashboard-project-delete-button-${project.id}`}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
